import React, { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { FeatureCollection, LineString } from 'geojson';
import { Box } from '@mui/material';

interface VineyardRowsOverlayProps {
  latitude: number;
  longitude: number;
  rowDirection: number;
  rowDistance: number;
  zoom: number;
}

/**
 * VineyardRowsOverlay - Renders vineyard rows
 * 
 * Follows best practices for Mapbox GL interactions and rendering efficiency
 */
const VineyardRowsOverlay: React.FC<VineyardRowsOverlayProps> = ({
  latitude,
  longitude,
  rowDirection,
  rowDistance,
  zoom
}) => {
  // Calculate geographic values in a single memoized function to improve performance
  const geoData = useMemo(() => {
    // Convert distance and calculate positions in one pass
    const metersPerDegree = 111000;
    const rowDistanceDegrees = (rowDistance / 100) / metersPerDegree;
    
    // Base length in meters, scaled by zoom
    const baseLengthMeters = 50;
    const baseLengthDegrees = baseLengthMeters / metersPerDegree;
    const rowLength = baseLengthDegrees * (16 / Math.max(zoom, 10));
    
    // Normalize the row direction to 0-359 range
    const normalizedRowDirection = ((rowDirection % 360) + 360) % 360;
    const directionRad = (normalizedRowDirection * Math.PI) / 180;
    
    // Calculate offsets for the three rows
    const offsets = [-rowDistanceDegrees, 0, rowDistanceDegrees];
    
    // Create GeoJSON features for all rows in one pass
    const features = offsets.map((offset) => {
      const perpDirRad = directionRad + Math.PI / 2;
      const offsetX = Math.sin(perpDirRad) * offset;  
      const offsetY = Math.cos(perpDirRad) * offset;
      
      const startX = longitude + offsetX - Math.sin(directionRad) * (rowLength / 2);
      const startY = latitude + offsetY - Math.cos(directionRad) * (rowLength / 2);
      const endX = longitude + offsetX + Math.sin(directionRad) * (rowLength / 2);
      const endY = latitude + offsetY + Math.cos(directionRad) * (rowLength / 2);
      
      return {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [startX, startY],
            [endX, endY]
          ]
        }
      };
    });
    
    return {
      rowsGeoJson: {
        type: 'FeatureCollection',
        features
      } as FeatureCollection<LineString>
    };
  }, [latitude, longitude, rowDirection, rowDistance, zoom]);

  return (
    <>
      {/* All GeoJSON layers for vineyard rows */}
      <Source id="vineyard-rows-source" type="geojson" data={geoData.rowsGeoJson}>
        {/* Ground overlay blur effect */}
        <Layer
          id="vineyard-area-layer"
          type="line"
          paint={{
            'line-color': 'rgba(220, 38, 38, 0.3)',
            'line-width': 12,
            'line-opacity': 0.4,
            'line-blur': 8
          }}
        />
        
        {/* Dashed line pattern */}
        <Layer
          id="vineyard-rows-dashed-layer"
          type="line"
          paint={{
            'line-color': 'rgba(220, 38, 38, 0.8)',
            'line-width': 2,
            'line-opacity': 0.8,
            'line-dasharray': [3, 2]
          }}
        />
        
        {/* Solid line */}
        <Layer
          id="vineyard-rows-layer"
          type="line"
          paint={{
            'line-color': 'rgba(185, 28, 28, 0.9)',
            'line-width': 3,
            'line-opacity': 1
          }}
        />
      </Source>
    </>
  );
};

export default VineyardRowsOverlay; 